/* eslint-disable no-param-reassign */
import { type EChartsExtensionInstallRegisters } from 'echarts/types/src/extension';
import type { Payload } from 'echarts/types/src/util/types';
import type GlobalModel from 'echarts/types/src/model/Global';
import type ExtensionAPI from 'echarts/types/src/core/ExtensionAPI';
import type { TreeNode } from 'echarts/types/src/data/Tree';
import type { Group } from 'zrender';
import { clearSelected, adapt } from './layoutHelper';
import {
  type TwoWayTreeSeriesModelClazz as TwoWayTreeSeriesModel,
  type TwoWayTreeSeriesNodeItemOption,
  setToInitialState
} from './TwoWayTreeSeries';
import { NODE_TYPE } from './twoWayTreeLayout';

type ActionPayload = Payload & {
  seriesIndex?: number | number[];
  seriesId?: string | string[];
  seriesName?: string | string[];
};

export function installTreeAction(registers: EChartsExtensionInstallRegisters) {
  //

  registers.registerAction(
    { type: 'dvTwoWayTreeExpandAllNode', update: 'update' },
    (payload: ActionPayload, ecModel: GlobalModel) => {
      const result = ecModel.findComponents({
        mainType: 'series',
        subType: 'dvTwoWayTree',
        query: {
          seriesIndex: payload.seriesIndex,
          seriesId: payload.seriesId,
          seriesName: payload.seriesName
        }
      }) as TwoWayTreeSeriesModel[];

      result.forEach(seriesModel => {
        const { leftTree } = seriesModel;
        const { rightTree } = seriesModel;

        leftTree.root.eachNode('preorder', (node: TreeNode) => {
          node.isExpand = true;
        });
        rightTree.root.eachNode('preorder', (node: TreeNode) => {
          node.isExpand = true;
        });
      });
    }
  );

  registers.registerAction(
    { type: 'dvTwoWayTreeExpandToInitDepth', update: 'update' },
    (payload: ActionPayload, ecModel: GlobalModel) => {
      const result = ecModel.findComponents({
        mainType: 'series',
        subType: 'dvTwoWayTree',
        query: {
          seriesIndex: payload.seriesIndex,
          seriesId: payload.seriesId,
          seriesName: payload.seriesName
        }
      }) as TwoWayTreeSeriesModel[];

      result.forEach(seriesModel => {
        setToInitialState(seriesModel);
      });
    }
  );

  type ExpandAndCollapseActionPayload = ActionPayload & {
    tree: 'left' | 'right';
    dataIndex: number;
  };

  registers.registerAction(
    { type: 'dvTwoWayTreeExpandAndCollapse', update: 'update' },
    (payload: ExpandAndCollapseActionPayload, ecModel: GlobalModel, api: ExtensionAPI) => {
      const result = ecModel.findComponents({
        mainType: 'series',
        subType: 'dvTwoWayTree',
        query: {
          seriesIndex: payload.seriesIndex,
          seriesId: payload.seriesId,
          seriesName: payload.seriesName
        }
      }) as TwoWayTreeSeriesModel[];

      result.forEach(seriesModel => {
        const tree = payload.tree === 'left' ? seriesModel.leftTree : seriesModel.rightTree;
        const node = tree.getNodeByDataIndex(payload.dataIndex);
        const nodeData = tree.data.getRawDataItem(
          payload.dataIndex
        ) as TwoWayTreeSeriesNodeItemOption;

        if (nodeData.type !== NODE_TYPE.GROUP) {
          return;
        }

        if (seriesModel.get('clearSelectedAfterClickGroupNode')) {
          clearSelected(seriesModel, api);
        }

        node.isExpand = !node.isExpand;
        // ! 要把组节点的子节点展开，否则无法通过交互展开子节点
        node.children.forEach(childNode => {
          childNode.isExpand = node.isExpand;
        });

        // ! 不加延迟此时 getItemGraphicEl 获取的是旧数据
        setTimeout(() => {
          let viewCenter = seriesModel.get('viewCenterAfterClick');
          if (typeof viewCenter === 'function') {
            viewCenter = viewCenter({
              node,
              data: tree.data.getRawDataItem(payload.dataIndex) as TwoWayTreeSeriesNodeItemOption,
              isExpand: node.isExpand
            }) as [number, number];
          }

          adapt(seriesModel, api, {
            animation: true,
            target: tree.data.getItemGraphicEl(payload.dataIndex) as Group,
            noScale: true,
            viewCenter
          });
        }, 50);
      });

      //
    }
  );

  //
}
